import type { Locale } from "@mw/shared/i18n";

import { getRegionLocale } from "@mw/shared/i18n";

export const formatNumberToKM = (number: number) => {
  if (number >= 1000000 && number < 1000000000) {
    return `${number / 1000000}M`;
  }
  if (number >= 1000 && number < 1000000) {
    return `${number / 1000}k`;
  }
  return `${number}`;
};

// Cache formatters across calls — calculators format prices on every slider tick,
// and `new Intl.NumberFormat(...)` is heavy enough that N formatters per render
// shows up on the drag path.
const PRICE_FORMATTER_CACHE = new Map<string, Intl.NumberFormat>();

const getPriceFormatter = (regionLocale: string, currencyCode: string) => {
  const key = `${regionLocale}|${currencyCode}`;
  const cached = PRICE_FORMATTER_CACHE.get(key);
  if (cached) return cached;
  const formatter = new Intl.NumberFormat(regionLocale, {
    style: "currency",
    currency: currencyCode,
    currencyDisplay: "narrowSymbol",
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });
  PRICE_FORMATTER_CACHE.set(key, formatter);
  return formatter;
};

export const formatPrice = (number: number, currency: null | string | undefined, locale: Locale) => {
  const regionLocale = getRegionLocale(locale);
  let currencyCode = "USD";

  if (currency === "$") {
    currencyCode = "USD";
  } else if (currency === "₹") {
    currencyCode = "INR";
  } else if (currency) {
    currencyCode = currency;
  }

  return getPriceFormatter(regionLocale, currencyCode).format(number);
};

const PRICE_PER_MINUTE_DECIMALS = 4;
const PRICE_PER_MINUTE_MISSING = "—";

/**
 * Formats a per-minute USD price to a fixed number of decimals (em dash when missing). Per-minute
 * LLM rates are sub-cent, so currency formatting (which rounds to cents) would collapse them —
 * hence a dedicated fixed-decimal helper used by the agents calculator.
 */
export const formatPricePerMinute = (value: number | undefined) => {
  if (typeof value !== "number" || !Number.isFinite(value)) return PRICE_PER_MINUTE_MISSING;
  return value.toFixed(PRICE_PER_MINUTE_DECIMALS);
};
